import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getCurrentProject, ProjectState} from '../../projects/store/reducers/projects.reducer';
import {DialogService} from '@shared/services/dialog-service';
import {VisualizerNotificationService} from '../Services/visualizer-notification.service';
import {QueryProvider} from '@shared/queries/query-provider';
import {take, takeUntil} from 'rxjs/operators';
import {ProjectModel} from '../../projects/models/projects.models';
import * as pbi from 'powerbi-client';
import {FilterType, Permissions, TokenType, ViewMode} from 'powerbi-models';
import {MatDialog} from '@angular/material/dialog';
import {AnalyticsService} from '@core/services/analytics.service';
import {ResetVisualizerDataCommand} from '@shared/commands/dashboard.command';
import {ResetDac6AnalyticsReport, ResetDac6AnalyticsReportError, ResetDac6AnalyticsReportSuccess} from '../store/action/dashboard-actions';
import {IDashboardState} from '../store/reducer/dashboard-reducer';
import {resetDac6AnalyticsError, resetDac6AnalyticsStatus, resetDac6AnalyticsSuccess} from '../store/selector/dashboard-selector';
import {DashboardService} from '@core/services/dashboard.service';
import {PlatformSidebarState} from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import {SetActivityFeedAction} from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import {PlatformCommand} from '@shared/commands/command-base';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {CommandService} from '@core/services/command.service';

@Component({
    standalone: false,
    selector: 'particle-dac6-analytics-v2',
  templateUrl: './dac6-analytics-v2.component.html',
  styleUrls: ['./dac6-analytics-v2.component.scss']
})
export class Dac6AnalyticsV2Component implements OnInit, OnDestroy {

  queryKey = 'dac6AnalyticsVisualizer';
  progress: string;
  dataLoaded: boolean;
  unsubscribe$ = new Subject<void>();
  queryParameters: Params;
  errorInit = false;
  editMode = false;
  report: any;
  failureResponse = false;
  haveQueryParams: boolean;
  currentProject: string;
  loadingPercentage: any = {};
  filterQueryParams: any;
  intervalTimer: any;
  initialEntry = true;
  analyticEndPointUrl: string;
  powerbiReportConfig: any = {};
  isVisitor: boolean;
  private analyticsTypeFromQuery: any;
  private reportType = 'DAC6';
  isEditable: boolean;
  private reportId: string;
  isDefaultReport = false;

  constructor(private securityStore: Store<SecurityState>,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private projectStore: Store<ProjectState>,
              protected dialogService: DialogService,
              protected dialog: MatDialog,
              private visualizerNotificationService: VisualizerNotificationService,
              private dashBoardService: DashboardService,
              private analyticsService: AnalyticsService,
              private platformSidebarStore: Store<PlatformSidebarState>,
              private dashboardStore: Store<IDashboardState>,
              private fuseSplashScreenService: FuseSplashScreenService,
              protected commandService: CommandService) {
  }

  ngOnInit() {
    this.failureResponse = false;
    this.dataLoaded = false;
    this.analyticEndPointUrl = QueryProvider.getPostCommandFullUrl(this.queryKey);
    this.loadingAnimation();
    this.projectStore.pipe(select(getCurrentProject), takeUntil(this.unsubscribe$)).subscribe((project: ProjectModel) => {
      if (project) {
        this.currentProject = project.ProjectId;
      }
    });
    const getQueryParams$ = this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe$));
    getQueryParams$.subscribe(queryParams => {
      if (queryParams.analyticsType) {
        this.analyticsTypeFromQuery = queryParams.analyticsType;
      }
    }, err => {
      console.log('query fetching failed');
    });
    this.handleResetReport();

    // const activityInfo = {type: 'Tool', 'id': 'dac6-analytics-DAC6', 'title': 'Analytics', isDefault: true};
    // this.platformSidebarStore.dispatch(new SetActivityFeedAction(activityInfo));
  }

  displayReport(reportConfig) {
    const filter: pbi.models.IBasicFilter = {
      $schema: 'http://powerbi.com/product/schema#basic',
      target: {
        table: 'Entities',
        column: 'ProjectId'
      },
      operator: 'In',
      values: [`${this.currentProject}`],
      filterType: FilterType.Basic
    };
    const config = <pbi.IEmbedConfiguration>{
      type: 'report',
      tokenType: TokenType.Embed,
      viewMode: ViewMode.View,
      permissions: Permissions.All,
      uniqueId: reportConfig.reportId,
      embedUrl: 'https://app.powerbi.com/reportEmbed?reportId=' + reportConfig.reportId + '&groupId=' + reportConfig.workspaceId,
      settings: {
        filterPaneEnabled: false
      },
      accessToken: reportConfig.token,
    };
    this.reportId = reportConfig.reportId;

    // Grab the reference to the div HTML element that will host the report.
    const reportsContainer = <HTMLElement>document.getElementById('reportsContainer');

    // Embed the report and display it within the div container.
    const powerbi = new pbi.service.Service(pbi.factories.hpmFactory, pbi.factories.wpmpFactory, pbi.factories.routerFactory);
    if (reportsContainer) {
      powerbi.reset(reportsContainer);
      this.report = powerbi.embed(reportsContainer, config);
      this.report.on('loaded', event => {
        this.postTabChangeAcknowledgement();
      });
      // this.setProjectFilter(filter);
      this.failureResponse = false;
      this.dataLoaded = true;
      if (this.analyticsTypeFromQuery) {
        this.goToPage(this.analyticsTypeFromQuery);
        this.analyticsTypeFromQuery = null;
      }
    }

  }

  private setProjectFilter(filter: pbi.models.IBasicFilter) {
    this.report.on('loaded', event => {
      this.report.getPages().then(pages =>
        pages.forEach(page => {
          page.removeFilters();
        })
      );
      const filters = ([{...filter}]);
      this.report.setFilters(filters);
    });
  }


  handleChange(event) {
    if (this.editMode) {
      this.report.switchMode('view');
    } else {
      this.report.switchMode('edit');
    }
    this.editMode = !this.editMode;
  }

  goToPage(pageName) {
    this.report.on('loaded', event => {
      this.report.getPages().then(pages => {
        pages.forEach(page => {
          if (page.displayName === pageName) {
            page.setActive().then(() => {
              console.log(`Active page has been set to ${page.displayName}`);
            }).catch(error => {
              console.log(`Error: ${error}. Can't set page to ${page.displayName}`);
            });
          }
        });
      });
     this.postTabChangeAcknowledgement();
    });
  }
  postTabChangeAcknowledgement() {
    this.report.on('pageChanged', event => {
      const page = event.detail.newPage.displayName;
      console.log('page Name : ', page);
      this.analyticsService.postAcknowledgeTabChangeCommand('DAC6 & MDR Reporter', page, this.reportId);
    });
  }
  loadingAnimation() {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
    }
    this.loadingPercentage.background = 'linear-gradient(to top, #337aee 0%, #eee 0%)';
    let timeInterval = 0;
    this.intervalTimer = setInterval(() => {
      timeInterval = timeInterval + 1.67;
      const timeIntervalString = timeInterval.toString() + '%';
      const backgroundGradient = `linear-gradient(to top, #337aee ${timeIntervalString}, #eee 0%`;
      this.loadingPercentage.background = backgroundGradient;
      // console.log(this.loadingPercentage.background);
      if (timeInterval >= 100 || this.dataLoaded || this.failureResponse) {
        clearInterval(this.intervalTimer);
      }
    }, 1000);
  }

  ngOnDestroy() {
    // this.visualizerNotificationService.destroyPulling();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  GetReportToken() {
    this.loadingAnimation();
    this.dashBoardService.getAnalyticsReportToken(this.reportType, 'Dac6').pipe(takeUntil(this.unsubscribe$))
      .subscribe((payload: any) => {
        if (payload) {
          this.dashboardStore.dispatch(new ResetDac6AnalyticsReportSuccess(payload));
        } else {
          this.dashboardStore.dispatch(new ResetDac6AnalyticsReportError(`Failed to get ReportToken for type ${this.reportType}`));
          console.error(`Failed to get ReportToken for type ${this.reportType}`);
        }
      }, err => {
        console.error('Error in geting ReportToken for PowerBi', err);
        this.dashboardStore.dispatch(new ResetDac6AnalyticsReportError(err));
      });
  }

  resetReport() {
    this.dataLoaded = false;
    this.failureResponse = false;
    this.loadingAnimation();
    const command = new ResetVisualizerDataCommand('DAC6');
    command.projectId = '[PROJECT_ID]';
    command.waitForResponse = false;
    this.dashboardStore.dispatch(new ResetDac6AnalyticsReport(command));
  }
  handleResetReport() {
    this.dashboardStore.pipe(select(resetDac6AnalyticsStatus), take(1)).subscribe(isResetOngoing => {
      if (isResetOngoing === false) {
        this.GetReportToken();
      }
    });
    this.dashboardStore.pipe(select(resetDac6AnalyticsSuccess), takeUntil(this.unsubscribe$)).subscribe(payload => {
      if (payload) {
        console.log('Reset visualizer Data command complete');
        this.powerbiReportConfig = payload.reportToken;
        this.isEditable = payload.isEditable;
        this.isDefaultReport = payload.isDefaultReport;
        this.dataLoaded = true;
        const hasReportUpdate = payload.hasReportUpdate;
        if (!hasReportUpdate) {
          this.displayReport(this.powerbiReportConfig);
        } else {
          this.dialogService.showYesNoDialog('Report Update Available', `There is an updated version of the report template available. <br /> Do you wish to update the report now?`).pipe(take(1))
            .subscribe(state => {
              if (state.action === 'OK') {
                this.fuseSplashScreenService.show();
                const command = new PlatformCommand('UpdateReportTemplateCommand', 'VisualizerApi');
                command.waitForResponse = true;
                command.setValue('ReportType', this.reportType);
                command.setValue('ReportConfigId', this.powerbiReportConfig.id);
                this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(commandState => {
                  const reportResponse = commandState.fieldValues['ReportResponse'];
                  if (reportResponse && reportResponse.reportToken) {
                    this.powerbiReportConfig = reportResponse.reportToken;
                  }
                  this.isEditable = reportResponse.isEditable;
                  this.isDefaultReport = reportResponse.isDefaultReport;
                  this.fuseSplashScreenService.hide();
                  this.displayReport(this.powerbiReportConfig);
                }, err => {
                  this.fuseSplashScreenService.hide();
                  this.displayReport(this.powerbiReportConfig);
                });
              } else {
                this.displayReport(this.powerbiReportConfig);
              }
            }, err => {
              this.fuseSplashScreenService.hide();
              this.displayReport(this.powerbiReportConfig);
            });
        }
       // this.displayReport(this.powerbiReportConfig);
      }
    });
    this.dashboardStore.pipe(select(resetDac6AnalyticsError), takeUntil(this.unsubscribe$)).subscribe(payload => {
      if (this.errorInit) {
        if (payload) {
          this.failureResponse = true;
          this.dataLoaded = false;
        }
      }
      this.errorInit = true;
    });

  }
}
